import moment from 'moment';
import Papa from 'papaparse';
import React, { memo, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { RouteLink } from '~/components';
import { useNumberFormat } from '~/hooks';
import { colors } from '~/styles';
import Table from './Table';

const PREVIEW_RESOURCES = 3;

// --- Pivot transform ---

function pivotScheduleData(data, meta) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const periods = [...new Set(data.map((r) => r.periodStart).filter(Boolean))].sort();
  if (periods.length === 0) return null;

  const unit = meta?.applied?.input?.unit || 'week';
  const resourceMap = new Map();

  // Detect whether resource grouping is active
  const hasResourceGrouping = data.some((r) => r.resourceId != null && r.rowType !== 'total');

  for (const row of data) {
    if (row.rowType === 'total') continue;

    if (hasResourceGrouping) {
      const isSubtotal = row.rowType === 'subtotal';
      const resId = row.resourceId;
      if (!resId) continue;

      if (!resourceMap.has(resId)) {
        resourceMap.set(resId, {
          resourceId: resId,
          resourceName: row.resourceName,
          resourceTypeId: row.resourceTypeId,
          periods: {},
          details: new Map(),
        });
      }
      const resource = resourceMap.get(resId);

      if (isSubtotal) {
        if (row.periodStart) {
          resource.periods[row.periodStart] = {
            allocated: row.allocated ?? 0,
            capacity: row.resourceTypeId === 'member' ? row.capacity : null,
            available: row.resourceTypeId === 'member' ? row.available : null,
          };
        }
      } else {
        const detailKey = row.projectId ?? row.timeOffTypeId ?? row.taskId ?? row.roleId ?? '__unassigned';
        const label = row.projectName ?? row.timeOffTypeName ?? row.taskName ?? row.roleName ?? 'Unassigned';

        if (!resource.details.has(detailKey)) {
          resource.details.set(detailKey, {
            label,
            projectId: row.projectId,
            clientId: row.clientId,
            clientName: row.clientName,
            periods: {},
          });
        }
        const detail = resource.details.get(detailKey);
        if (row.periodStart) {
          detail.periods[row.periodStart] = { allocated: row.allocated ?? 0 };
        }
      }
    } else {
      // No resource grouping — put all rows as details under a single synthetic resource.
      // Skip rows with no assignment dimensions (collapsed subtotal remnants).
      const detailKey = row.projectId ?? row.timeOffTypeId ?? row.taskId ?? row.roleId;
      if (!detailKey) continue;

      const syntheticId = '__no_resource';
      if (!resourceMap.has(syntheticId)) {
        resourceMap.set(syntheticId, {
          resourceId: syntheticId,
          resourceName: null,
          resourceTypeId: 'member',
          periods: {},
          details: new Map(),
        });
      }
      const resource = resourceMap.get(syntheticId);
      const label = row.projectName ?? row.timeOffTypeName ?? row.taskName ?? row.roleName ?? 'Unassigned';

      if (!resource.details.has(detailKey)) {
        resource.details.set(detailKey, {
          label,
          projectId: row.projectId,
          clientId: row.clientId,
          clientName: row.clientName,
          periods: {},
        });
      }
      const detail = resource.details.get(detailKey);
      if (row.periodStart) {
        detail.periods[row.periodStart] = { allocated: row.allocated ?? 0 };
      }
    }
  }

  // Compute totals per resource and convert detail Maps to sorted arrays
  const allResources = [...resourceMap.values()]
    .map((r) => {
      const isMember = r.resourceTypeId === 'member';
      const hasOwnPeriods = Object.keys(r.periods).length > 0;

      const details = [...r.details.values()]
        .map((d) => {
          let total = 0;
          for (const p of periods) {
            total += d.periods[p]?.allocated ?? 0;
          }
          return { ...d, total };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      // Compute resource totals from its own periods (subtotals) or from details if no resource grouping
      const totals = { allocated: 0, capacity: null, available: null };
      if (hasOwnPeriods) {
        for (const p of periods) {
          const pd = r.periods[p];
          if (pd) {
            totals.allocated += pd.allocated;
            if (isMember && pd.capacity != null) {
              totals.capacity = (totals.capacity ?? 0) + pd.capacity;
              totals.available = (totals.available ?? 0) + (pd.available ?? 0);
            }
          }
        }
      } else {
        // Sum from details (no resource grouping)
        for (const p of periods) {
          let pAllocated = 0;
          for (const d of details) {
            pAllocated += d.periods[p]?.allocated ?? 0;
          }
          r.periods[p] = { allocated: pAllocated };
          totals.allocated += pAllocated;
        }
      }

      return { ...r, details, totals, hasCapacity: isMember && totals.capacity != null };
    })
    .sort((a, b) => a.resourceName.localeCompare(b.resourceName));

  // Split into members and placeholders
  const members = allResources.filter((r) => r.resourceTypeId === 'member');
  const placeholders = allResources.filter((r) => r.resourceTypeId === 'placeholder');

  // Compute section subtotals
  function sectionTotals(resources) {
    const st = { periods: {}, totals: { allocated: 0, capacity: 0, available: 0 } };
    for (const p of periods) {
      let pAllocated = 0;
      let pCapacity = 0;
      let pAvailable = 0;
      for (const r of resources) {
        const pd = r.periods[p];
        if (pd) {
          pAllocated += pd.allocated;
          pCapacity += pd.capacity ?? 0;
          pAvailable += pd.available ?? 0;
        }
      }
      st.periods[p] = { allocated: pAllocated, capacity: pCapacity, available: pAvailable };
      st.totals.allocated += pAllocated;
      st.totals.capacity += pCapacity;
      st.totals.available += pAvailable;
    }
    return st;
  }

  const memberTotals = members.length > 0 ? sectionTotals(members) : null;
  const placeholderTotals = placeholders.length > 0 ? sectionTotals(placeholders) : null;

  // Grand total from both sections
  const grandTotal = { periods: {}, totals: { allocated: 0, capacity: 0, available: 0 } };
  for (const p of periods) {
    const m = memberTotals?.periods[p];
    const pl = placeholderTotals?.periods[p];
    grandTotal.periods[p] = {
      allocated: (m?.allocated ?? 0) + (pl?.allocated ?? 0),
      capacity: (m?.capacity ?? 0) + (pl?.capacity ?? 0),
      available: (m?.available ?? 0) + (pl?.available ?? 0),
    };
    grandTotal.totals.allocated += grandTotal.periods[p].allocated;
    grandTotal.totals.capacity += grandTotal.periods[p].capacity;
    grandTotal.totals.available += grandTotal.periods[p].available;
  }

  return { periods, unit, members, placeholders, memberTotals, placeholderTotals, grandTotal };
}

function formatPeriod(periodStart, unit) {
  const d = moment(periodStart);
  switch (unit) {
    case 'month':
      return d.format('MMM');
    case 'day':
    case 'week':
    default:
      return d.format('MMM D');
  }
}

// --- Styled components ---

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
    overflow-anchor: none;
  `,

  TableScroll: styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;
    transition: max-height 0.3s ease;

    > table {
      min-width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
      table-layout: auto;

      th,
      td {
        padding: 0.375rem 0.5rem;
        white-space: nowrap;
        border-right: 1px solid ${colors.grey5};
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      th {
        font-weight: 600;
        background: ${colors.grey5};
        border-bottom: 2px solid ${colors.grey20};
        text-align: right;
      }

      td {
        border-bottom: 1px solid ${colors.grey10};
        text-align: right;
      }

      th:first-child,
      td:first-child {
        text-align: left;
        min-width: 12rem;
        position: sticky;
        left: 0;
        background: ${colors.white};
        z-index: 1;
        border-right: 1px solid ${colors.grey10};
      }

      th:first-child {
        background: ${colors.grey5};
        z-index: 2;
      }

      tr:hover td {
        background: ${colors.grey5};
      }

      tr:hover td:first-child {
        background: ${colors.grey5};
      }

      .subtotal-row td {
        font-weight: 500;
      }

      .subtotal-row td:first-child {
        font-weight: 600;
      }

      .section-header td {
        font-weight: 600;
        font-size: 0.6875rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: ${colors.grey55};
        background: ${colors.grey5};
        border-bottom: 1px solid ${colors.grey20};
      }

      .section-header td:first-child {
        background: ${colors.grey5};
      }

      .total-row td {
        font-weight: 600;
        border-top: 2px solid ${colors.grey20};
      }
    }
  `,

  FadeOverlay: styled.div`
    position: sticky;
    left: 0;
    height: 4.5rem;
    margin-top: -4.5rem;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    pointer-events: none;
  `,

  CapacityAnnotation: styled.span`
    font-size: 0.625rem;
    font-weight: 400;
    color: ${({ $danger }) => ($danger ? colors.danger : colors.grey40)};
    display: block;
    margin-top: 0.0625rem;
  `,

  DetailLabel: styled.div`
    padding-left: 1rem;
  `,

  DetailClient: styled.div`
    padding-left: 1rem;
    font-size: 0.625rem;
    color: ${colors.grey40};
  `,

  ToggleButton: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,

  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  `,
};

// --- Cell formatting ---

function MetricCell({ value }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });
  if (value == null || value === 0) return <td />;
  return <td>{numberFormat.format(value)}</td>;
}

// --- Section rendering ---

function SectionHeader({ label, sectionTotals, periods, showCapacity }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <tr className="section-header">
      <td>
        {label}
        {showCapacity && sectionTotals && (
          <S.CapacityAnnotation $danger={sectionTotals.totals.available < 0}>
            {numberFormat.format(sectionTotals.totals.capacity)}h capacity &middot;{' '}
            {numberFormat.format(sectionTotals.totals.available)}h available
          </S.CapacityAnnotation>
        )}
      </td>
      {periods.map((p) => {
        const val = sectionTotals?.periods[p]?.allocated;
        return <td key={p}>{val ? numberFormat.format(val) : ''}</td>;
      })}
      <td>{sectionTotals ? numberFormat.format(sectionTotals.totals.allocated) : ''}</td>
    </tr>
  );
}

function ResourceSection({ resources, periods, workspaceKey }) {
  return resources.flatMap((resource) => [
    ...(resource.resourceName
      ? [<ResourceRow key={`r-${resource.resourceId}`} resource={resource} periods={periods} />]
      : []),
    ...resource.details.map((detail, di) => (
      <DetailRow key={`d-${resource.resourceId}-${di}`} detail={detail} periods={periods} workspaceKey={workspaceKey} />
    )),
  ]);
}

// --- Component ---

function AssistantSchedule({ data, fields, meta, pagination, filename }) {
  const pivot = useMemo(() => pivotScheduleData(data, meta), [data, meta]);
  const tableScrollRef = useRef(null);
  const resourceCount = pivot ? pivot.members.length + pivot.placeholders.length : 0;
  const [isExpanded, setIsExpanded] = useState(resourceCount <= 6);
  const canCollapse = resourceCount > PREVIEW_RESOURCES;

  // Fall back to flat table when there are no period columns to pivot
  if (!pivot) {
    return <Table data={data} fields={fields} meta={meta} pagination={pagination} filename={filename} />;
  }

  const { periods, unit, members, placeholders, memberTotals, placeholderTotals, grandTotal } = pivot;
  const workspaceKey = meta?.workspaceKey;

  // For collapse, count visible resources across both sections
  let visibleMembers = members;
  let visiblePlaceholders = placeholders;
  if (!isExpanded && canCollapse) {
    if (members.length >= PREVIEW_RESOURCES) {
      visibleMembers = members.slice(0, PREVIEW_RESOURCES);
      visiblePlaceholders = [];
    } else {
      visibleMembers = members;
      visiblePlaceholders = placeholders.slice(0, PREVIEW_RESOURCES - members.length);
    }
  }

  const hasBothSections = members.length > 0 && placeholders.length > 0;

  const handleToggle = (expand) => {
    const el = tableScrollRef.current;
    if (!el) {
      setIsExpanded(expand);
      return;
    }

    if (expand) {
      el.style.transition = 'max-height 0.5s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      setIsExpanded(true);
      requestAnimationFrame(() => {
        el.style.maxHeight = `${el.scrollHeight}px`;
        const onEnd = () => {
          el.style.maxHeight = 'none';
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    } else {
      const table = el.querySelector('table');
      const thead = table?.querySelector('thead');
      const tbodyRows = table?.querySelectorAll('tbody tr');
      let targetHeight = el.scrollHeight;
      if (thead && tbodyRows) {
        targetHeight = thead.offsetHeight;
        let resCount = 0;
        for (let i = 0; i < tbodyRows.length && resCount < PREVIEW_RESOURCES; i++) {
          targetHeight += tbodyRows[i].offsetHeight;
          if (tbodyRows[i].classList.contains('subtotal-row')) resCount++;
        }
      }

      el.style.transition = 'max-height 0.25s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.style.maxHeight = `${targetHeight}px`;
        const onEnd = () => {
          setIsExpanded(false);
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    }
  };

  const handleDownload = () => {
    const csvRows = [];
    const allResources = [...members, ...placeholders];
    for (const r of allResources) {
      const resourceRow = {
        Section: r.resourceTypeId === 'member' ? 'Members' : 'Placeholders',
        Resource: r.resourceName,
        Project: '',
      };
      for (const p of periods) {
        resourceRow[formatPeriod(p, unit)] = r.periods[p]?.allocated ?? '';
      }
      resourceRow.Total = r.totals.allocated;
      csvRows.push(resourceRow);

      for (const d of r.details) {
        const detailRow = { Section: '', Resource: '', Project: d.label };
        for (const p of periods) {
          detailRow[formatPeriod(p, unit)] = d.periods[p]?.allocated ?? '';
        }
        detailRow.Total = d.total;
        csvRows.push(detailRow);
      }
    }

    const totalRow = { Section: '', Resource: 'Total', Project: '' };
    for (const p of periods) {
      totalRow[formatPeriod(p, unit)] = grandTotal.periods[p]?.allocated ?? '';
    }
    totalRow.Total = grandTotal.totals.allocated;
    csvRows.push(totalRow);

    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'schedule'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remainingCount = resourceCount - PREVIEW_RESOURCES;

  return (
    <S.Container>
      <S.TableScroll ref={tableScrollRef}>
        <table>
          <thead>
            <tr>
              <th>Resource</th>
              {periods.map((p) => (
                <th key={p}>{formatPeriod(p, unit)}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {visibleMembers.length > 0 && (
              <>
                {hasBothSections && (
                  <SectionHeader label="Members" sectionTotals={memberTotals} periods={periods} showCapacity={true} />
                )}
                <ResourceSection resources={visibleMembers} periods={periods} workspaceKey={workspaceKey} />
              </>
            )}
            {visiblePlaceholders.length > 0 && (
              <>
                {hasBothSections && (
                  <SectionHeader
                    label="Placeholders"
                    sectionTotals={placeholderTotals}
                    periods={periods}
                    showCapacity={false}
                  />
                )}
                <ResourceSection resources={visiblePlaceholders} periods={periods} workspaceKey={workspaceKey} />
              </>
            )}
            <TotalRow grandTotal={grandTotal} periods={periods} />
          </tbody>
        </table>
        {!isExpanded && canCollapse && <S.FadeOverlay />}
      </S.TableScroll>
      <S.ButtonRow>
        {canCollapse &&
          (isExpanded ? (
            <S.ToggleButton onClick={() => handleToggle(false)}>Show less ({resourceCount} resources)</S.ToggleButton>
          ) : (
            <S.ToggleButton onClick={() => handleToggle(true)}>
              Show all ({remainingCount} more resources)
            </S.ToggleButton>
          ))}
        <S.ToggleButton onClick={handleDownload}>Save CSV</S.ToggleButton>
      </S.ButtonRow>
    </S.Container>
  );
}

function ResourceRow({ resource, periods }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const { totals, hasCapacity } = resource;

  return (
    <tr className="subtotal-row">
      <td>
        {resource.resourceName}
        {hasCapacity && (
          <S.CapacityAnnotation $danger={totals.available != null && totals.available < 0}>
            {numberFormat.format(totals.capacity)}h capacity &middot; {numberFormat.format(totals.available)}h available
          </S.CapacityAnnotation>
        )}
      </td>
      {periods.map((p) => (
        <MetricCell key={p} value={resource.periods[p]?.allocated} />
      ))}
      <td>{numberFormat.format(totals.allocated)}</td>
    </tr>
  );
}

function DetailRow({ detail, periods, workspaceKey }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const projectLink =
    detail.projectId && workspaceKey ? `/app/${workspaceKey}/portfolio/projects/${detail.projectId}` : null;
  const clientLink =
    detail.clientId && workspaceKey ? `/app/${workspaceKey}/portfolio/clients/${detail.clientId}` : null;

  return (
    <tr>
      <td>
        <S.DetailLabel>
          {projectLink ? <RouteLink to={projectLink}>{detail.label}</RouteLink> : detail.label}
        </S.DetailLabel>
        {detail.clientName && (
          <S.DetailClient>
            {clientLink ? <RouteLink to={clientLink}>{detail.clientName}</RouteLink> : detail.clientName}
          </S.DetailClient>
        )}
      </td>
      {periods.map((p) => (
        <MetricCell key={p} value={detail.periods[p]?.allocated} />
      ))}
      <td>{detail.total > 0 ? numberFormat.format(detail.total) : ''}</td>
    </tr>
  );
}

function TotalRow({ grandTotal, periods }) {
  const numberFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <tr className="total-row">
      <td>Total</td>
      {periods.map((p) => {
        const val = grandTotal.periods[p]?.allocated;
        return <td key={p}>{val ? numberFormat.format(val) : ''}</td>;
      })}
      <td>{numberFormat.format(grandTotal.totals.allocated)}</td>
    </tr>
  );
}

export default memo(AssistantSchedule);
